import { useState, useEffect, useCallback, useRef } from 'react';
import type { TimeEntry, ISODateString, BankHoliday } from '../types/timesheet.types';
import { TimesheetStatus, ProjectType, toISODateString } from '../types/timesheet.types';
import type { SuperApprovalItem, ApprovalSource } from '../types/approval.types';
import { useConnector } from '../contexts/ConnectorContext';
import { WorkflowTriggerType } from '../types/connector.types';
import { getHoursByProjectType } from '../utils/calculations';
import { buildWeeklySummaries } from '../utils/approvalTransform';
import { VALIDATION, SP_LISTS } from '../utils/constants';
import { errorLogger } from '../utils/errorLogger';

interface State {
  pendingItems: SuperApprovalItem[];
  reviewedItems: SuperApprovalItem[];
  isLoading: boolean;
  isReviewedLoading: boolean;
  reviewedLoaded: boolean;
  loadError: Error | null;
  isActioning: boolean;
  actionError: Error | null;
}

export interface UseSuperApprovalsResult {
  pendingItems: SuperApprovalItem[];
  reviewedItems: SuperApprovalItem[];
  isLoading: boolean;
  isReviewedLoading: boolean;
  loadError: Error | null;
  isActioning: boolean;
  actionError: Error | null;
  superApproveMonth: (item: SuperApprovalItem, approverName: string, approverEmail: string) => Promise<void>;
  rejectMonth: (item: SuperApprovalItem, reason: string, approverName: string, approverEmail: string) => Promise<void>;
  unlockMonth: (item: SuperApprovalItem, reason: string, approvedBy: string, approverEmail: string) => Promise<void>;
  loadReviewed: () => Promise<void>;
  refresh: () => Promise<void>;
}

type PersonLike = { fullName: string; m365Email: string; department: string; id: string };
type PersonLookup = ReadonlyMap<string, PersonLike>;
type ProjectTypeMap = ReadonlyMap<string, { type: ProjectType }>;

function listNameFor(source: ApprovalSource): string {
  return source === 'asset' ? SP_LISTS.ASSET_TIMESHEETS : SP_LISTS.TIME_ENTRIES;
}

export function buildPersonLookup(people: readonly PersonLike[]): PersonLookup {
  const map = new Map<string, PersonLike>();
  for (const p of people) {
    if (p.id) map.set(p.id, p);
    if (p.m365Email) map.set(p.m365Email.toLowerCase(), p);
  }
  return map;
}

function lookupPerson(personMap: PersonLookup, personId: string): PersonLike | undefined {
  return personMap.get(personId) ?? (personId ? personMap.get(personId.toLowerCase()) : undefined);
}

export function buildSuperApprovalCards(
  entries: readonly TimeEntry[],
  projectTypeMap: ProjectTypeMap,
  personMap: PersonLookup,
  source: ApprovalSource,
  bankHolidays: readonly BankHoliday[] = []
): SuperApprovalItem[] {
  const grouped = new Map<string, TimeEntry[]>();
  for (const entry of entries) {
    const assetKey = source === 'asset' ? entry.assetCode || '(no asset)' : '';
    const key = `${entry.personEmail}::${entry.periodMonth}::${assetKey}`;
    const bucket = grouped.get(key);
    if (bucket) bucket.push(entry);
    else grouped.set(key, [entry]);
  }

  const result: SuperApprovalItem[] = [];
  for (const [, groupEntries] of grouped) {
    const first = groupEntries[0];
    const person = lookupPerson(personMap, first.personEmail);
    const personName = person?.fullName ?? first.personName;
    const personEmail = person?.m365Email ?? first.personEmail;
    const department = person?.department ?? '';

    result.push({
      personId: person?.id ?? first.personEmail,
      personName,
      personEmail,
      department,
      periodMonth: first.periodMonth,
      totalHours: groupEntries.reduce((s, e) => s + e.hours, 0),
      status: first.status,
      submittedDate: first.submittedDate,
      managerApprovedDate: first.managerApprovedDate,
      managerName: first.managerName,
      superApprovedDate: first.superApprovedDate,
      superApproverName: first.superApproverName,
      breakdownByType: getHoursByProjectType(groupEntries, projectTypeMap),
      weeks: buildWeeklySummaries(groupEntries, projectTypeMap, bankHolidays, first.periodMonth),
      source,
      assetCode: source === 'asset' ? first.assetCode : '',
      isBackupApproval: false,
    });
  }

  return result.sort((a, b) => (a.managerApprovedDate ?? '').localeCompare(b.managerApprovedDate ?? ''));
}

export function useSuperApprovals(superApproverEmail: string, softLaunchMode = false): UseSuperApprovalsResult {
  const connector = useConnector();
  const reviewingRef = useRef(false);

  const [state, setState] = useState<State>({
    pendingItems: [],
    reviewedItems: [],
    isLoading: true,
    isReviewedLoading: false,
    reviewedLoaded: false,
    loadError: null,
    isActioning: false,
    actionError: null,
  });

  const load = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true, loadError: null }));
    try {
      const currentYear = new Date().getUTCFullYear();

      if (softLaunchMode) {
        const [allProjects, allPeople, empEntries, astEntries, bhThisYear, bhLastYear] = await Promise.all([
          connector.getAllActiveProjects(),
          connector.getPeople({ active: true }),
          connector.getTimeEntries({ status: TimesheetStatus.Submitted }),
          connector.getTimeEntries({ status: TimesheetStatus.Submitted }, SP_LISTS.ASSET_TIMESHEETS),
          connector.getBankHolidays(currentYear),
          connector.getBankHolidays(currentYear - 1),
        ]);
        const bankHolidays: BankHoliday[] = [...bhThisYear, ...bhLastYear];
        const projectTypeMap = new Map(allProjects.map(p => [p.projectCode, { type: p.type }]));
        const personMap = buildPersonLookup(allPeople);
        const pendingItems = [
          ...buildSuperApprovalCards(empEntries, projectTypeMap, personMap, 'employee', bankHolidays),
          ...buildSuperApprovalCards(astEntries, projectTypeMap, personMap, 'asset', bankHolidays),
        ].sort((a, b) => (a.submittedDate ?? '').localeCompare(b.submittedDate ?? ''));
        setState(s => ({ ...s, pendingItems, isLoading: false }));
        return;
      }

      const [allProjects, superReports, bhThisYear, bhLastYear] = await Promise.all([
        connector.getAllActiveProjects(),
        connector.getSuperApproverReports(superApproverEmail),
        connector.getBankHolidays(currentYear),
        connector.getBankHolidays(currentYear - 1),
      ]);
      const bankHolidays: BankHoliday[] = [...bhThisYear, ...bhLastYear];

      const [empArrays, astArrays] = await Promise.all([
        Promise.all(superReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.ManagerApproved, personEmail: p.m365Email }))),
        Promise.all(superReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.ManagerApproved, personEmail: p.m365Email }, SP_LISTS.ASSET_TIMESHEETS))),
      ]);

      const projectTypeMap = new Map(allProjects.map(p => [p.projectCode, { type: p.type }]));
      const personMap = buildPersonLookup(superReports);
      const pendingItems = [
        ...buildSuperApprovalCards((empArrays as TimeEntry[][]).flat(), projectTypeMap, personMap, 'employee', bankHolidays),
        ...buildSuperApprovalCards((astArrays as TimeEntry[][]).flat(), projectTypeMap, personMap, 'asset', bankHolidays),
      ];

      setState(s => ({ ...s, pendingItems, isLoading: false }));
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      errorLogger.log(error, { component: 'useSuperApprovals', action: 'load', userEmail: superApproverEmail });
      setState(s => ({ ...s, isLoading: false, loadError: error }));
    }
  }, [connector, superApproverEmail, softLaunchMode]);

  const loadReviewed = useCallback(async () => {
    if (reviewingRef.current) return;
    setState(s => { if (s.reviewedLoaded) return s; return { ...s, isReviewedLoading: true }; });
    reviewingRef.current = true;
    try {
      const currentYear = new Date().getUTCFullYear();

      if (softLaunchMode) {
        const [allProjects, allPeople, empSuperEntries, empRejEntries, astSuperEntries, astRejEntries, bhThisYear, bhLastYear] = await Promise.all([
          connector.getAllActiveProjects(),
          connector.getPeople({ active: true }),
          connector.getTimeEntries({ status: TimesheetStatus.SuperApproved }),
          connector.getTimeEntries({ status: TimesheetStatus.Rejected }),
          connector.getTimeEntries({ status: TimesheetStatus.SuperApproved }, SP_LISTS.ASSET_TIMESHEETS),
          connector.getTimeEntries({ status: TimesheetStatus.Rejected }, SP_LISTS.ASSET_TIMESHEETS),
          connector.getBankHolidays(currentYear),
          connector.getBankHolidays(currentYear - 1),
        ]);
        const bankHolidays: BankHoliday[] = [...bhThisYear, ...bhLastYear];
        const projectTypeMap = new Map(allProjects.map(p => [p.projectCode, { type: p.type }]));
        const personMap = buildPersonLookup(allPeople);
        const reviewedItems = [
          ...buildSuperApprovalCards([...empSuperEntries, ...empRejEntries], projectTypeMap, personMap, 'employee', bankHolidays),
          ...buildSuperApprovalCards([...astSuperEntries, ...astRejEntries], projectTypeMap, personMap, 'asset', bankHolidays),
        ];
        setState(s => ({ ...s, reviewedItems, isReviewedLoading: false, reviewedLoaded: true }));
        return;
      }

      const [allProjects, superReports, bhThisYear, bhLastYear] = await Promise.all([
        connector.getAllActiveProjects(),
        connector.getSuperApproverReports(superApproverEmail),
        connector.getBankHolidays(currentYear),
        connector.getBankHolidays(currentYear - 1),
      ]);
      const bankHolidays: BankHoliday[] = [...bhThisYear, ...bhLastYear];

      const [empSuperArrays, empRejArrays, astSuperArrays, astRejArrays] = await Promise.all([
        Promise.all(superReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.SuperApproved, personEmail: p.m365Email }))),
        Promise.all(superReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.Rejected, personEmail: p.m365Email }))),
        Promise.all(superReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.SuperApproved, personEmail: p.m365Email }, SP_LISTS.ASSET_TIMESHEETS))),
        Promise.all(superReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.Rejected, personEmail: p.m365Email }, SP_LISTS.ASSET_TIMESHEETS))),
      ]);

      const projectTypeMap = new Map(allProjects.map(p => [p.projectCode, { type: p.type }]));
      const personMap = buildPersonLookup(superReports);
      const reviewedItems = [
        ...buildSuperApprovalCards(
          [...(empSuperArrays as TimeEntry[][]).flat(), ...(empRejArrays as TimeEntry[][]).flat()],
          projectTypeMap, personMap, 'employee', bankHolidays
        ),
        ...buildSuperApprovalCards(
          [...(astSuperArrays as TimeEntry[][]).flat(), ...(astRejArrays as TimeEntry[][]).flat()],
          projectTypeMap, personMap, 'asset', bankHolidays
        ),
      ];

      setState(s => ({ ...s, reviewedItems, isReviewedLoading: false, reviewedLoaded: true }));
    } catch (err) {
      errorLogger.log(err, { component: 'useSuperApprovals', action: 'loadReviewed', userEmail: superApproverEmail });
      reviewingRef.current = false;
      setState(s => ({ ...s, isReviewedLoading: false }));
    }
  }, [connector, superApproverEmail, softLaunchMode]);

  useEffect(() => { void load(); }, [load]);

  const superApproveMonth = useCallback(
    async (item: SuperApprovalItem, approverName: string, approverEmail: string): Promise<void> => {
      const fromSubmitted = softLaunchMode && item.status === TimesheetStatus.Submitted;
      if (!fromSubmitted && item.status !== TimesheetStatus.ManagerApproved) {
        throw new Error('Can only super-approve a Manager Approved timesheet.');
      }

      const entries = item.weeks.flatMap(w => [...w.entries]);
      const now = toISODateString(new Date().toISOString().slice(0, 10));
      const listName = listNameFor(item.source);

      setState(s => ({ ...s, isActioning: true, actionError: null }));
      try {
        await connector.batchUpdateEntries(
          entries.map(e => ({ id: e.id, fields: {
            status: TimesheetStatus.SuperApproved,
            superApprovedDate: now,
            superApproverName: approverName,
            superApproverEmail: approverEmail,
            lockedDate: now,
            ...(fromSubmitted ? { managerApprovedDate: now, managerName: approverName, managerEmail: approverEmail } : {}),
          }})),
          listName
        );
        const weekSummary = JSON.stringify(
          item.weeks.map(w => ({ label: w.label, totalHours: w.totalHours }))
        );
        await connector.triggerWorkflow({
          triggerType: WorkflowTriggerType.SuperApproveMonth,
          personId: item.personId,
          userName: item.personName,
          userEmail: item.personEmail,
          periodMonth: item.periodMonth,
          actionStatus: 'Super Approved',
          isFinalState: true,
          approverName,
          approverEmail,
          approverRole: 'Super Approver',
          source: item.source === 'asset' ? 'AssetTimesheet' : 'SuperApprover',
          totalHours: item.totalHours,
          weekSummary,
        });
        setState(s => ({
          ...s,
          pendingItems: s.pendingItems.filter(
            a => !(a.personId === item.personId
              && a.periodMonth === item.periodMonth
              && a.source === item.source
              && a.assetCode === item.assetCode)
          ),
          isActioning: false,
        }));
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        errorLogger.log(error, { component: 'useSuperApprovals', action: 'superApproveMonth', userEmail: approverEmail });
        setState(s => ({ ...s, isActioning: false, actionError: error }));
        throw error;
      }
    },
    [connector, softLaunchMode]
  );

  const rejectMonth = useCallback(
    async (item: SuperApprovalItem, reason: string, approverName: string, approverEmail: string): Promise<void> => {
      if (reason.trim().length < VALIDATION.MIN_REJECTION_REASON_LENGTH) {
        throw new Error(`Reason must be at least ${VALIDATION.MIN_REJECTION_REASON_LENGTH} characters.`);
      }

      if (item.status !== TimesheetStatus.ManagerApproved
          && !(softLaunchMode && item.status === TimesheetStatus.Submitted)) {
        throw new Error('Can only reject a Manager Approved timesheet.');
      }

      const entries = item.weeks.flatMap(w => [...w.entries]);
      const listName = listNameFor(item.source);

      setState(s => ({ ...s, isActioning: true, actionError: null }));
      try {
        await connector.batchUpdateEntries(
          entries.map(e => ({ id: e.id, fields: {
            status: TimesheetStatus.Rejected,
            rejectionNote: reason,
            submittedDate: null,
            managerApprovedDate: null,
          }})),
          listName
        );
        await connector.triggerWorkflow({
          triggerType: WorkflowTriggerType.RejectMonth,
          personId: item.personId,
          userName: item.personName,
          userEmail: item.personEmail,
          periodMonth: item.periodMonth,
          actionStatus: 'Rejected',
          isFinalState: false,
          approverName,
          approverEmail,
          approverRole: 'Super Approver',
          rejectionComment: reason,
          reason,
          source: item.source === 'asset' ? 'AssetTimesheet' : 'SuperApprover',
          totalHours: item.totalHours,
        });
        setState(s => ({
          ...s,
          pendingItems: s.pendingItems.filter(
            a => !(a.personId === item.personId
              && a.periodMonth === item.periodMonth
              && a.source === item.source
              && a.assetCode === item.assetCode)
          ),
          isActioning: false,
        }));
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        errorLogger.log(error, { component: 'useSuperApprovals', action: 'rejectMonth', userEmail: approverEmail });
        setState(s => ({ ...s, isActioning: false, actionError: error }));
        throw error;
      }
    },
    [connector, softLaunchMode]
  );

  const unlockMonth = useCallback(
    async (item: SuperApprovalItem, reason: string, approvedBy: string, approverEmail: string): Promise<void> => {
      if (!reason.trim()) {
        throw new Error('Unlock reason is required.');
      }

      const now = toISODateString(new Date().toISOString().slice(0, 10));
      const listName = listNameFor(item.source);

      setState(s => ({ ...s, isActioning: true, actionError: null }));
      try {
        const allEntries = await connector.getTimeEntries(
          { personEmail: item.personEmail, periodMonth: item.periodMonth, status: TimesheetStatus.SuperApproved },
          listName,
        );
        const entries = item.source === 'asset'
          ? allEntries.filter(e => e.assetCode === item.assetCode)
          : allEntries;
        if (entries.length === 0) {
          throw new Error('No Super Approved entries found to unlock.');
        }
        await connector.batchUpdateEntries(
          entries.map(e => ({ id: e.id, fields: {
            status: TimesheetStatus.Draft,
            unlockedDate: now,
            unlockReason: reason,
            unlockApprovedBy: approvedBy,
            lockedDate: null,
            superApprovedDate: null,
            managerApprovedDate: null,
            submittedDate: null,
          }})),
          listName
        );

        // Capture unlock audit records — one record per month timesheet
        const auditRecords = [{
          timeEntryId: 0,
          personName: item.personName,
          personEmail: item.personEmail,
          dateWorked: `${item.periodMonth}-01` as ISODateString,
          periodMonth: item.periodMonth,
          oldValue: 'SuperApproved',
          newValue: 'Draft',
          editorName: approvedBy,
          editorEmail: approverEmail,
          editedDate: now,
          editReasonNotes: reason,
        }];
        try {
          await connector.batchSaveAuditRecords(auditRecords);
        } catch (err) {
          // Log but don't fail the unlock if audit save fails
          errorLogger.log(err instanceof Error ? err : new Error(String(err)), {
            component: 'useSuperApprovals',
            action: 'unlockMonth - batchSaveAuditRecords',
            userEmail: approverEmail,
          });
        }

        await connector.triggerWorkflow({
          triggerType: WorkflowTriggerType.UnlockMonth,
          personId: item.personId,
          userName: item.personName,
          userEmail: item.personEmail,
          periodMonth: item.periodMonth,
          actionStatus: 'Draft',
          isFinalState: false,
          approverName: approvedBy,
          approverEmail,
          approverRole: 'Super Approver',
          reason,
          source: item.source === 'asset' ? 'AssetTimesheet' : 'SuperApprover',
        });
        setState(s => ({
          ...s,
          reviewedItems: s.reviewedItems.map(a =>
            a.personId === item.personId
              && a.periodMonth === item.periodMonth
              && a.source === item.source
              && a.assetCode === item.assetCode
              ? { ...a, status: TimesheetStatus.Draft }
              : a
          ),
          isActioning: false,
        }));
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        errorLogger.log(error, { component: 'useSuperApprovals', action: 'unlockMonth', userEmail: approverEmail });
        setState(s => ({ ...s, isActioning: false, actionError: error }));
        throw error;
      }
    },
    [connector]
  );

  return {
    pendingItems: state.pendingItems,
    reviewedItems: state.reviewedItems,
    isLoading: state.isLoading,
    isReviewedLoading: state.isReviewedLoading,
    loadError: state.loadError,
    isActioning: state.isActioning,
    actionError: state.actionError,
    superApproveMonth,
    rejectMonth,
    unlockMonth,
    loadReviewed,
    refresh: load,
  };
}
